import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue } from "framer-motion";
import LiveSignal from "@/components/LiveSignal";
import useMediaQuery from "@/hooks/useMediaQuery";

const PLAN_V1 = [
  "Strength · 45 min · 17:30",
  "Zone 2 · 20 min · morning",
  "Protein target · 140 g",
  "Sleep window · 22:45",
];

const PLAN_V2 = [
  "Strength · 25 min express · 19:00",
  "Zone 2 · moved to tomorrow",
  "Protein target · 140 g",
  "Sleep window · 22:45",
];

const PlanCard = ({ p }) => {
  const disrupt = useTransform(p, [0.13, 0.17], [0, 1]);
  const v1 = useTransform(p, [0.18, 0.22], [1, 0]);
  const v2 = useTransform(p, [0.19, 0.23], [0, 1]);
  return (
    <div className="w-full max-w-md rounded-xl border border-line bg-ink-2/60 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[10px] tracking-[0.22em] text-white">
          TODAY · PLAN 04
        </p>
        <span className="flex items-center gap-2">
          <LiveSignal className="h-4 w-16" color="#D2378B" />
          <span className="font-mono text-[9px] tracking-[0.16em] text-ash">
            TWIN STATE · LIVE
          </span>
        </span>
      </div>
      <motion.p
        style={{ opacity: disrupt }}
        className="mt-5 rounded-md border border-white/25 px-3 py-2 font-mono text-[9px] leading-relaxed tracking-[0.14em] text-white"
      >
        DISRUPTION · MEETING ADDED · 17:00–18:30
      </motion.p>
      <div className="relative mt-5">
        <motion.ul style={{ opacity: v1 }} className="space-y-px">
          {PLAN_V1.map((r) => (
            <li
              key={r}
              className="rounded-md border border-line/70 px-4 py-3 text-sm text-fog"
            >
              {r}
            </li>
          ))}
        </motion.ul>
        <motion.ul style={{ opacity: v2 }} className="absolute inset-0 space-y-px">
          {PLAN_V2.map((r, i) => (
            <li
              key={r}
              className={`rounded-md border px-4 py-3 text-sm ${
                i < 2
                  ? "border-white/30 text-white"
                  : "border-line/70 text-fog"
              }`}
            >
              {r}
            </li>
          ))}
        </motion.ul>
      </div>
      <motion.p
        style={{ opacity: v2 }}
        className="mt-4 font-mono text-[9px] tracking-[0.18em] text-signal/80"
      >
        PLAN ADAPTED · 19:04
      </motion.p>
    </div>
  );
};

const CoachCard = ({ p }) => {
  const q = useTransform(p, [0.37, 0.41], [0, 1]);
  const a1 = useTransform(p, [0.44, 0.48], [0, 1]);
  const a2 = useTransform(p, [0.49, 0.53], [0, 1]);
  return (
    <div className="w-full max-w-md">
      <div className="flex items-center gap-4">
        <p className="font-mono text-[9px] leading-relaxed tracking-[0.16em] text-ash">
          CONTEXT · SLEEP 6H 12M · TRAVEL DAY · STRENGTH BLOCK W3
        </p>
        <LiveSignal className="h-4 w-16 shrink-0" />
      </div>
      <motion.div style={{ opacity: q }} className="mt-6 flex justify-end">
        <p className="max-w-[80%] rounded-2xl rounded-br-md border border-white/30 px-5 py-4 text-sm leading-relaxed text-white">
          I only have 10 minutes. What should I do?
        </p>
      </motion.div>
      <motion.div style={{ opacity: a1 }} className="mt-4 flex">
        <p className="max-w-[85%] rounded-2xl rounded-bl-md border border-line bg-ink-2/70 px-5 py-4 text-sm leading-relaxed text-fog">
          Do the 10-minute hotel-room circuit · push-ups, split squats, plank.
        </p>
      </motion.div>
      <motion.div style={{ opacity: a2 }} className="mt-4 flex">
        <p className="max-w-[85%] rounded-2xl rounded-bl-md border border-line bg-ink-2/70 px-5 py-4 text-sm leading-relaxed text-fog">
          It keeps your strength block on track, fits the time you actually
          have, and leaves tomorrow&apos;s session untouched.
        </p>
      </motion.div>
    </div>
  );
};

const SimulatorCard = ({ p }) => {
  const draw = useTransform(p, [0.7, 0.86], [0, 1]);
  const note = useTransform(p, [0.86, 0.92], [0, 1]);
  return (
    <div className="w-full max-w-xl rounded-xl border border-line bg-ink-2/50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[10px] tracking-[0.22em] text-white">
          SIMULATION · 12 WEEKS
        </p>
        <p className="font-mono text-[9px] tracking-[0.16em] text-ash">
          OUTPUT · PREDICTION
        </p>
      </div>
      <svg viewBox="0 0 400 240" className="mt-6 w-full" aria-hidden="true">
        <line x1="20" y1="20" x2="20" y2="220" stroke="#2A2026" strokeWidth="1" />
        <line x1="20" y1="220" x2="390" y2="220" stroke="#2A2026" strokeWidth="1" />
        {/* NO CHANGE · flat baseline */}
        <motion.path
          d="M20 210 C140 206 250 202 380 198"
          stroke="#3A3238"
          strokeWidth="1"
          strokeDasharray="4 4"
          fill="none"
          style={{ pathLength: draw }}
        />
        {/* HIIT PLAN · fast rise, plateau earlier */}
        <motion.path
          d="M20 210 C90 120 200 78 380 70"
          stroke="#6E676A"
          strokeWidth="1.2"
          fill="none"
          style={{ pathLength: draw }}
        />
        {/* STRENGTH PLAN · steadier, ends higher */}
        <motion.path
          d="M20 210 C140 176 240 116 380 44"
          stroke="#D2378B"
          strokeWidth="1.5"
          fill="none"
          style={{ pathLength: draw }}
        />
        <motion.g style={{ opacity: note }}>
          <circle cx="380" cy="44" r="6" fill="#D2378B" opacity="0.35" className="pulse-ring" />
          <circle cx="380" cy="44" r="2.5" fill="#D2378B" />
        </motion.g>
        <text x="382" y="40" fill="#D2378B" fontSize="9" fontFamily="JetBrains Mono" textAnchor="end">STRENGTH PLAN</text>
        <text x="382" y="66" fill="#A19A9D" fontSize="9" fontFamily="JetBrains Mono" textAnchor="end">HIIT PLAN</text>
        <text x="382" y="194" fill="#6E676A" fontSize="9" fontFamily="JetBrains Mono" textAnchor="end">NO CHANGE</text>
        <text x="390" y="234" fill="#6E676A" fontSize="8" fontFamily="JetBrains Mono" textAnchor="end">WEEKS →</text>
        <text x="14" y="16" fill="#6E676A" fontSize="8" fontFamily="JetBrains Mono">PERFORMANCE</text>
      </svg>
      <motion.p
        style={{ opacity: note }}
        className="mt-4 font-mono text-[9px] leading-relaxed tracking-[0.18em] text-fog"
      >
        <span className="text-signal">STRENGTH PLAN</span> · MOST REALISTIC TO MAINTAIN
        IN YOUR ACTUAL LIFE
      </motion.p>
    </div>
  );
};

const STATES = [
  {
    label: "CAPABILITY 01 · PLANNING",
    title: "A plan shaped by the version of you the twin has been watching.",
    body: "Wednesday evenings vanish. Sunday mornings are yours. The twin has watched a year of it. Today&apos;s plan starts where your week actually starts.",
  },
  {
    label: "CAPABILITY 02 · TWIN",
    title: "The twin has already been paying attention.",
    body: "Ask a question and the answer already has your Monday in it. It knows you slept badly, skipped the swim, and finally cooked at home. Every reply is picking up where your last week left off.",
  },
  {
    label: "CAPABILITY 03 · WHAT IF?",
    title: "Try the decision before you make it.",
    body: "Move the workout to Thursday. Drop the wine for a fortnight. Add a walk after dinner. The twin runs it forward before you commit, and shows what shifts in your body if you do.",
  },
];

const CapabilitiesPinned = () => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const s1 = useTransform(p, [0, 0.03, 0.28, 0.33], [0, 1, 1, 0]);
  const s2 = useTransform(p, [0.33, 0.37, 0.6, 0.65], [0, 1, 1, 0]);
  const s3 = useTransform(p, [0.66, 0.7], [0, 1]);

  const states = [
    { style: { opacity: s1 }, visual: <PlanCard p={p} />, ...STATES[0] },
    { style: { opacity: s2 }, visual: <CoachCard p={p} />, ...STATES[1] },
    { style: { opacity: s3 }, visual: <SimulatorCard p={p} />, ...STATES[2] },
  ];

  return (
    <section
      ref={ref}
      data-testid="capabilities-section"
      className="relative h-[460vh]"
    >
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <div className="px-12 pt-20">
          <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
            03 · CAPABILITIES
          </p>
          <h2 className="mt-3 max-w-md font-display text-xl font-light text-white lg:text-2xl">
            What becomes possible when your system actually understands you?
          </h2>
        </div>
        <div className="relative min-h-0 flex-1">
          {states.map((s, i) => (
            <motion.div
              key={i}
              style={s.style}
              className="absolute inset-0 flex items-center"
            >
              <div className="mx-auto grid w-full max-w-[1300px] items-center gap-10 px-12 lg:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.24em] text-ash">
                    {s.label}
                  </p>
                  <h3 className="mt-4 font-display text-[clamp(1.35rem,2.4vw,2.25rem)] font-light leading-snug text-white">
                    {s.title}
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-fog">
                    {s.body}
                  </p>
                </div>
                <div className="flex justify-center">{s.visual}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CapabilitiesStacked = () => {
  const done = useMotionValue(1);
  const visuals = [
    <PlanCard key="plan" p={done} />,
    <CoachCard key="coach" p={done} />,
    <SimulatorCard key="sim" p={done} />,
  ];
  return (
    <section
      data-testid="capabilities-section-mobile"
      className="mx-auto max-w-2xl space-y-16 px-6 py-24 md:px-10"
    >
      <div>
        <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
          03 · CAPABILITIES
        </p>
        <h2 className="mt-4 font-display text-2xl font-light leading-snug text-white">
          What becomes possible when your system actually understands you?
        </h2>
      </div>
      {STATES.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[10px] tracking-[0.22em] text-ash">
            {s.label}
          </p>
          <h3 className="mt-4 font-display text-2xl font-light leading-snug text-white">
            {s.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-fog">{s.body}</p>
          <div className="mt-8">{visuals[i]}</div>
        </motion.div>
      ))}
    </section>
  );
};

const Capabilities = () => {
  const pinned = useMediaQuery("(min-width: 900px) and (min-height: 640px)");
  return pinned ? <CapabilitiesPinned /> : <CapabilitiesStacked />;
};

export default Capabilities;
